import * as React from 'react';
import { useCallback } from 'react';
import { observer } from 'mobx-react';

import { Context } from 'bernie-context';
import { QueryRoute } from 'bernie-core';
import { Layer, Viewport, ViewMedium, ViewSmall } from 'bernie-view';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { TypeaheadRenderType, TypeaheadSelection } from 'uitk-react-typeahead';
import { UitkIconSize } from 'uitk-react-icons';
import { liveAnnounce, Level } from 'uitk-react-utils';

import { LocationInput } from '.';
import { TertiaryButtonTracked } from 'components/shared/tracked';
import { FlightsLocation, flightsSimpleLocalizationTemplate, trackSearchFormAnalytics } from 'utility';
import { useStores } from 'utility/hooks';

import { FlightsSearchFormLocationFieldType } from 'types/search/wizard';
import { FlightsShoppingPwaFlightSearchResultsFlightsSearch } from '__generated__/api';
import SwapLocationsAction = FlightsShoppingPwaFlightSearchResultsFlightsSearch.SwapLocationsAction;
import { InteractableFieldComponentProps } from 'types/shared';

export interface LocationsProps extends InteractableFieldComponentProps {
  journeyIndex: number;
  originLocation: FlightsSearchFormLocationFieldType;
  destinationLocation: FlightsSearchFormLocationFieldType;
  context: Context;
  essEndpoint: string;
  onSetOriginRef: (ref: React.Ref<HTMLButtonElement>) => void;
  onSetDestinationRef: (ref: React.Ref<HTMLButtonElement>) => void;
  swapLocationsAction: SwapLocationsAction;
}

export const Locations = observer(
  ({
    journeyIndex,
    originLocation,
    destinationLocation,
    context,
    essEndpoint,
    onSetOriginRef,
    onSetDestinationRef,
    onFieldInteraction,
    swapLocationsAction
  }: LocationsProps) => {
    const { analytics, routeStore, searchFormStore } = useStores();
    const { smallView: swapSmallViewIcon } = swapLocationsAction.icon;
    const { largeView: swapLargeViewIcon } = swapLocationsAction.icon;
    const { accessibilityMessage, accessibilityUsageMessage, analytics: swapAnalytics } = swapLocationsAction;

    const onTypeaheadDialogDismiss = useCallback(
      renderType => {
        if (renderType === TypeaheadRenderType.DIALOG) {
          routeStore.goBack();
        }
      },
      [routeStore]
    );

    const updateOriginLocation = useCallback(
      (selectionTerm: string) => {
        if (originLocation.value !== selectionTerm) {
          trackSearchFormAnalytics(
            analytics,
            originLocation.analytics,
            searchFormStore.selectedTripTypeOptionItem.tripType,
            journeyIndex + 1
          );
        }
        searchFormStore.setLocation(journeyIndex, FlightsLocation.ORIGIN, selectionTerm);
      },
      [analytics, journeyIndex, originLocation.analytics, originLocation.value, searchFormStore]
    );

    const updateDestinationLocation = useCallback(
      (selectionTerm: string) => {
        if (destinationLocation.value !== selectionTerm) {
          trackSearchFormAnalytics(
            analytics,
            destinationLocation.analytics,
            searchFormStore.selectedTripTypeOptionItem.tripType,
            journeyIndex + 1
          );
        }
        searchFormStore.setLocation(journeyIndex, FlightsLocation.DESTINATION, selectionTerm);
      },
      [analytics, journeyIndex, destinationLocation.analytics, destinationLocation.value, searchFormStore]
    );

    const onOriginInputChange = () => {
      if (searchFormStore.userStartedLocationTyping) {
        trackSearchFormAnalytics(
          analytics,
          originLocation.onTypeAnalytics,
          searchFormStore.selectedTripTypeOptionItem.tripType,
          journeyIndex + 1
        );
        searchFormStore.setUserStartedLocationTyping(false);
      }
    };

    const onDestinationInputChange = () => {
      if (searchFormStore.userStartedLocationTyping) {
        trackSearchFormAnalytics(
          analytics,
          destinationLocation.onTypeAnalytics,
          searchFormStore.selectedTripTypeOptionItem.tripType,
          journeyIndex + 1
        );
        searchFormStore.setUserStartedLocationTyping(false);
      }
    };

    const onOriginUpdate = useCallback(
      (selection: TypeaheadSelection) => {
        updateOriginLocation(selection.term);
      },
      [updateOriginLocation]
    );

    const onDestinationUpdate = useCallback(
      (selection: TypeaheadSelection) => {
        updateDestinationLocation(selection.term);
      },
      [updateDestinationLocation]
    );

    const renderDialogMarkup = (dialogId: string, children: JSX.Element) => {
      return (
        <QueryRoute query={{ pwaDialog: dialogId }}>
          <Layer id={dialogId} onDismiss={onTypeaheadDialogDismiss}>
            {children}
          </Layer>
        </QueryRoute>
      );
    };

    const originDialogId = `originLocation-dialog-${journeyIndex}`;
    const destinationDialogId = `destinationLocation-dialog-${journeyIndex}`;

    const renderOriginLocationDialog = (children: JSX.Element) => {
      return renderDialogMarkup(originDialogId, children);
    };

    const renderDestinationLocationDialog = (children: JSX.Element) => {
      return renderDialogMarkup(destinationDialogId, children);
    };

    const openTypeahead = typeaheadDialogId => {
      if (routeStore && typeof routeStore.updateSearch === 'function') {
        routeStore.updateSearch({ pwaDialog: typeaheadDialogId });
      }
    };

    const openOriginTypeahead = (renderType: string) => {
      if (renderType === TypeaheadRenderType.DIALOG) {
        openTypeahead(originDialogId);
      }
      onFieldInteraction && onFieldInteraction();
      searchFormStore.setUserStartedLocationTyping(true);
    };

    const openDestinationTypeahead = (renderType: string) => {
      if (renderType === TypeaheadRenderType.DIALOG) {
        openTypeahead(destinationDialogId);
      }
      onFieldInteraction && onFieldInteraction();
      searchFormStore.setUserStartedLocationTyping(true);
    };

    const swapOriginAndDestination = () => {
      const previousOrigin = originLocation.value;
      updateOriginLocation(destinationLocation.value);
      updateDestinationLocation(previousOrigin);
    };

    const isSwapOriginAndDestinationDisabled =
      originLocation.value.length === 0 && destinationLocation.value.length === 0;

    const getUsageMessage = () => {
      const replacerStrings = [destinationLocation.value, originLocation.value];

      return flightsSimpleLocalizationTemplate(accessibilityUsageMessage, replacerStrings);
    };

    const swapActionHandleOnClick = () => {
      const usageMessage = getUsageMessage();
      onFieldInteraction && onFieldInteraction();
      swapOriginAndDestination();

      trackSearchFormAnalytics(
        analytics,
        swapAnalytics,
        searchFormStore.selectedTripTypeOptionItem.tripType,
        journeyIndex + 1
      );
      // TODO: sync with CSE on a better way to handle this
      // setting it any lower the message gets over taken by typeahead announce
      setTimeout(() => liveAnnounce(usageMessage, Level.ASSERTIVE), 500);
    };

    const swapSmallViewIconSize = UitkIconSize[swapSmallViewIcon.size];
    const swapLargeViewIconSize = UitkIconSize[swapLargeViewIcon.size];

    const originLocationInput = (
      <div data-test-id="typeahead-originInput">
        <LocationInput
          location={originLocation}
          context={context}
          onClick={openOriginTypeahead}
          onInputChange={onOriginInputChange}
          renderDialog={renderOriginLocationDialog}
          onDismiss={onTypeaheadDialogDismiss}
          onSetOriginRef={onSetOriginRef}
          name="originInput"
          stid={`typeahead-originInput-${journeyIndex}`}
          onUpdate={onOriginUpdate}
          essEndpoint={essEndpoint}
        />
      </div>
    );

    const destinationLocationInput = (
      <div data-test-id="typeahead-destinationInput">
        <LocationInput
          location={destinationLocation}
          context={context}
          onClick={openDestinationTypeahead}
          onInputChange={onDestinationInputChange}
          renderDialog={renderDestinationLocationDialog}
          dest={true}
          onDismiss={onTypeaheadDialogDismiss}
          onSetDestinationRef={onSetDestinationRef}
          name="destinationInput"
          stid={`typeahead-destinationInput-${journeyIndex}`}
          onUpdate={onDestinationUpdate}
          essEndpoint={essEndpoint}
        />
      </div>
    );

    const swapLocationsButton = (iconId: string, iconSize: UitkIconSize) => (
      <TertiaryButtonTracked
        icon={iconId}
        data-test-id="swap-locations-action"
        onClick={swapActionHandleOnClick}
        disabled={isSwapOriginAndDestinationDisabled}
        iconSize={iconSize}
        aria-label={accessibilityMessage}
      />
    );

    return (
      <Viewport>
        <ViewSmall>
          <UitkLayoutGrid columns={8} rows={2} space="three">
            <UitkLayoutGridItem colSpan={7} rowSpan={1}>
              {originLocationInput}
            </UitkLayoutGridItem>
            <UitkLayoutGridItem colSpan={1} rowSpan={2}>
              {swapLocationsButton(swapSmallViewIcon.id, swapSmallViewIconSize)}
            </UitkLayoutGridItem>
            <UitkLayoutGridItem colSpan={7} rowSpan={1}>
              {destinationLocationInput}
            </UitkLayoutGridItem>
          </UitkLayoutGrid>
        </ViewSmall>
        <ViewMedium>
          <UitkLayoutGrid columns={{ auto_fit: 'zero' }} rows={1}>
            <UitkLayoutGridItem colSpan={{ medium: 5, large: 6, extra_large: 7 }}>
              {originLocationInput}
            </UitkLayoutGridItem>
            <UitkLayoutGridItem colSpan={1}>
              {swapLocationsButton(swapLargeViewIcon.id, swapLargeViewIconSize)}
            </UitkLayoutGridItem>
            <UitkLayoutGridItem colSpan={{ medium: 5, large: 6, extra_large: 7 }}>
              {destinationLocationInput}
            </UitkLayoutGridItem>
          </UitkLayoutGrid>
        </ViewMedium>
      </Viewport>
    );
  }
);
